import { DeepMap, FieldError, FieldValues } from 'react-hook-form';

import { notifyError } from 'shared/Toast/Toast';

import { logDataDogEvent } from './datadog';

export const CONTROLLED_SUBSTANCE_PHOTO_ID_REQUIRED = 'CONTROLLED_SUBSTANCE_PHOTO_ID_REQUIRED';

/**
 * Extract the nested API `errorCode` from an RTK Query / fetch rejection payload.
 *
 * Backend shape for typed errors (e.g. ControlledSubstancePhotoIdRequiredError):
 * `{ data: { message, data: { errorCode } } }`
 */
export const getApiErrorCode = (error: unknown): string | undefined => {
  if (!error || typeof error !== 'object' || !('data' in error)) {
    return undefined;
  }
  const data = (error as { data?: unknown }).data;
  if (!data || typeof data !== 'object' || !('data' in data)) {
    return undefined;
  }
  const nested = (data as { data?: unknown }).data;
  if (!nested || typeof nested !== 'object' || !('errorCode' in nested)) {
    return undefined;
  }
  const errorCode = (nested as { errorCode?: unknown }).errorCode;
  return typeof errorCode === 'string' ? errorCode : undefined;
};

export const isControlledSubstancePhotoIdRequiredError = (error: unknown): boolean =>
  getApiErrorCode(error) === CONTROLLED_SUBSTANCE_PHOTO_ID_REQUIRED;

export const handleRequestCatch = (
  e: MessageEvent,
  alternativeMessage = 'Something went wrong, please try again later'
) => {
  const getErrorMessage = () => {
    if ('status' in e && e.status === 'FETCH_ERROR') {
      return 'Fetch error';
    }
    if (typeof e.data === 'string') {
      return e.data;
    }
    if ('message' in e && typeof e.message === 'string') {
      return e.message;
    }
    if ('data' in e && e.data && 'message' in e.data && typeof e.data.message === 'string') {
      return e.data.message;
    }
    return 'Uncaught Error';
  };
  logDataDogEvent({
    context: {
      componentStack: 'handleRequestCatch',
      ...e
    },
    error: new Error(getErrorMessage()),
    level: 'warn',
    message: getErrorMessage()
  });
  if ('status' in e && e.status === 500) {
    return;
  }
  notifyError(e.data?.message || alternativeMessage);
};

export const onFormError = (errors: DeepMap<FieldValues, FieldError>) => {
  requestAnimationFrame(() => {
    const fieldNames = Object.keys(errors);

    const firstErrorName = fieldNames.find((name) => errors[name]);
    if (firstErrorName) {
      const element = document.querySelector(`[name="${firstErrorName}"]`);

      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  });
};
